import React, { memo, useEffect, useRef, useState } from 'react';
import { getGoogleMapsApiKey, loadGoogleMaps } from '../../../utils/helpers/googleMapsLoader';
import styles from '../../../styles/components/forms/BookingForm.module.css';

/** Limits Place Details on selection (same idea as Angular `Autocomplete` `fields`). */
const AUTOCOMPLETE_OPTIONS = {
    // United Kingdom only (`gb` is the ISO 3166-1 alpha-2 code for the UK)
    componentRestrictions: { country: 'gb' },
    // Omit `types` so all prediction types are allowed (equivalent to `types: []` in Angular).
    fields: ['geometry', 'formatted_address', 'address_components', 'name'],
};

// Custom Places Autocomplete component with native Google Maps - moved outside to prevent re-renders
const PlacesAutocomplete = memo(({ field, placeholder, value, onChange, className, error, isDarkMode, onClearError, onErrorChange }) => {
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const isSelectingPlace = useRef(false);
    const [inputValue, setInputValue] = useState(value || '');
    const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
    const [pasteError, setPasteError] = useState('');
    const [manipulationError, setManipulationError] = useState('');
    const [originalSelectedValue, setOriginalSelectedValue] = useState('');
    const [hasSelectedPlace, setHasSelectedPlace] = useState(false);

    // Load Google Maps only when user focuses on this input
    const loadGoogleMapsIfNeeded = async () => {
        try {
            const apiKey = getGoogleMapsApiKey();
            if (!apiKey) {
                console.error('Google Maps API key not found');
                return;
            }

            await loadGoogleMaps(apiKey, ['places']);
        } catch (error) {
            console.error('Failed to load Google Maps:');
        }
    };

    const handleFocus = async () => {
        if (!isGoogleMapsLoaded) {
            await loadGoogleMapsIfNeeded();
            setIsGoogleMapsLoaded(true);
        }
    };

    useEffect(() => {
        if (isGoogleMapsLoaded && window.google && inputRef.current && !autocompleteRef.current) {
            // Initialize autocomplete
            autocompleteRef.current = new window.google.maps.places.Autocomplete(
                inputRef.current,
                AUTOCOMPLETE_OPTIONS
            );

            // Add place changed listener
            autocompleteRef.current.addListener('place_changed', () => {
                const place = autocompleteRef.current.getPlace();
                if (place && place.formatted_address && place.geometry) {
                    // Set flag to prevent value override
                    isSelectingPlace.current = true;
                    
                    // Store the original selected value for validation
                    setOriginalSelectedValue(place.formatted_address);
                    setHasSelectedPlace(true);
                    
                    // Immediately update input value
                    setInputValue(place.formatted_address);

                    const onChangePayload = {
                        value: {
                            description: place.formatted_address,
                            geometry: place.geometry,
                        },
                    };
                    onChange(field, onChangePayload);

                    // Clear all errors when place is selected
                    setPasteError('');
                    setManipulationError('');
                    if (onClearError) {
                        onClearError(field);
                    }
                    // Clear paste and manipulation errors from parent state
                    if (onErrorChange) {
                        onErrorChange(field, 'pasteError', '');
                        onErrorChange(field, 'manipulationError', '');
                    }

                    // Reset flag after a short delay
                    setTimeout(() => {
                        isSelectingPlace.current = false;
                    }, 100);
                } 
            });
        }
    }, [isGoogleMapsLoaded, field, onChange, onClearError, onErrorChange]);

    const handleInputChange = e => {
        const newValue = e.target.value;
        setInputValue(newValue);
        isSelectingPlace.current = false; // Reset place selected when user types
        
        // Check if user is manipulating a previously selected place
        if (hasSelectedPlace && originalSelectedValue && newValue !== originalSelectedValue) {
            setManipulationError('Please select a valid address from the dropdown. Do not modify the selected address.');
            setHasSelectedPlace(false); // Reset the flag since user is now typing
        }
        
        // Clear paste error when user starts typing
        if (pasteError) {
            setPasteError('');
        }
    };

    const handlePaste = e => {
        e.preventDefault();
        setPasteError('Please type and select from the dropdown instead of pasting text.');
    };

    const handleKeyDown = e => {
        // Prevent Ctrl+V, Cmd+V, and right-click paste
        if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
            e.preventDefault();
            setPasteError('Please type and select from the dropdown instead of pasting text.');
        }
        
        // Check for backspace or delete when a place is selected
        if ((e.key === 'Backspace' || e.key === 'Delete') && hasSelectedPlace && originalSelectedValue) {
            setManipulationError('Please select a valid address from the dropdown. Do not modify the selected address.');
            setHasSelectedPlace(false);
        }
    };

    // Update input value when prop value changes, but not during place selection
    useEffect(() => {
        if (!isSelectingPlace.current) {
            setInputValue(value || '');
            // Reset selection state when value is cleared externally
            if (!value) {
                setHasSelectedPlace(false);
                setOriginalSelectedValue('');
                setManipulationError('');
            }
        }
    }, [value]);

    // Notify parent component when paste error changes
    useEffect(() => {
        if (onErrorChange) {
            onErrorChange(field, 'pasteError', pasteError);
        }
    }, [pasteError, field, onErrorChange]);

    // Notify parent component when manipulation error changes
    useEffect(() => {
        if (onErrorChange) {
            onErrorChange(field, 'manipulationError', manipulationError);
        }
    }, [manipulationError, field, onErrorChange]);

    return (
        <div>
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                className={`${className} ${isDarkMode ? 'input-dark' : ''}`}
                style={{
                    width: '100%',
                    height: '42px',
                    padding: '0.5rem',
                    border: (error || pasteError || manipulationError) ? '1px solid #dc3545' : '0.5px solid #e6e6e6',
                    borderRadius: '5px',
                    fontSize: '14px',
                    fontFamily: 'Gilroy-Regular',
                    backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
                    color: isDarkMode ? '#ffffff' : '#000000',
                }}
            />
            {/* {pasteError && (
                <div
                    className={`text-danger error-message-container ${styles['error-message-container']}`}
                    style={{ minHeight: '20px' }}>
                    {pasteError}
                </div>
            )}
            {manipulationError && (
                <div
                    className={`text-danger error-message-container ${styles['error-message-container']}`}
                    style={{ minHeight: '20px' }}>
                    {manipulationError}
                </div>
            )} */}
            <style jsx>{`
                :global(.pac-container) {
                    z-index: 10000 !important;
                    ${isDarkMode ? `
                        background-color: #2a2a2a !important;
                    ` : ''}
                }
                
                :global(.pac-container .pac-item) {
                    ${isDarkMode ? `
                        background-color: #2a2a2a !important;
                        color: #ffffff !important;
                    ` : ''}
                }
                
                :global(.pac-container .pac-item:hover) {
                    ${isDarkMode ? `
                        background-color: #3a3a3a !important;
                    ` : ''}
                }
                
                :global(.pac-container .pac-item-selected) {
                    ${isDarkMode ? `
                        background-color: #3a3a3a !important;
                    ` : ''}
                }
                
                :global(.pac-container .pac-item-query) {
                    ${isDarkMode ? `
                        color: #ffffff !important;
                    ` : ''}
                }
                
                :global(.pac-container .pac-matched) {
                    ${isDarkMode ? `
                        color: #ff6b35 !important;
                    ` : ''}
                }
            `}</style>
        </div>
    );
});

PlacesAutocomplete.displayName = 'PlacesAutocomplete';

export default PlacesAutocomplete;
